/**
 * The contact as a form, and what changed in it (Phase 144).
 *
 * Pure, and separate from the panel for the reason every form in this codebase keeps its draft
 * apart from its markup: the interesting failures are "it saved a field nobody touched" and "it
 * lost one somebody did", and neither is visible in JSX.
 */

export const PERSON_TYPES = ['INDIVIDUAL', 'COMPANY'] as const;

export type PersonType = (typeof PERSON_TYPES)[number];

export interface ContactForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  jobTitle: string;
  companyId: string | null;
  personType: PersonType | '';
  document: string;
  sourceId: string | null;
  lifecycleStage: string;
}

/** The record as the server sends it: snake case, nulls for absence. */
export type ContactRecord = Record<string, unknown>;

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function idOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function formOf(record: ContactRecord): ContactForm {
  return {
    firstName: str(record.first_name),
    lastName: str(record.last_name),
    email: str(record.email),
    phone: str(record.phone),
    mobile: str(record.mobile),
    jobTitle: str(record.job_title),
    companyId: idOrNull(record.company_id),
    personType: (PERSON_TYPES as readonly string[]).includes(str(record.person_type))
      ? (str(record.person_type) as PersonType)
      : '',
    document: str(record.document),
    sourceId: idOrNull(record.source_id),
    lifecycleStage: str(record.lifecycle_stage) || 'LEAD',
  };
}

export type FormProblem = 'name_required' | 'email_invalid' | 'document_too_short';

/**
 * What is wrong with the draft, if anything.
 *
 * The e-mail rule is the one worth stating: **empty is allowed**, because most people who write on
 * WhatsApp never give one, and refusing to save a name because an optional field is blank is how a
 * form teaches somebody not to use it. Filled, it has to look like an address.
 */
export function problemsWith(form: ContactForm): FormProblem | null {
  if (form.firstName.trim() === '') return 'name_required';

  const email = form.email.trim();

  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return 'email_invalid';

  const document = form.document.replace(/\D/g, '');

  // Eleven digits is a CPF, fourteen a CNPJ. Anything shorter that is not empty is a typo.
  if (document !== '' && document.length < 11) return 'document_too_short';

  return null;
}

/**
 * Only what changed, in the shape the server's schema accepts.
 *
 * Sending the whole form would rewrite fields nobody touched — which is invisible until two people
 * have the same contact open, and then it is one of them silently undoing the other. An emptied box
 * is a real change and travels as `null`, because "apagar o cargo" has to be possible.
 */
export function changesBetween(
  base: ContactForm,
  next: ContactForm,
): Record<string, unknown> | null {
  const changes: Record<string, unknown> = {};

  const text = (key: keyof ContactForm, field: string): void => {
    const before = String(base[key] ?? '').trim();
    const after = String(next[key] ?? '').trim();

    if (before === after) return;

    changes[field] = after === '' ? null : after;
  };

  // The one field that cannot be cleared: a contact with no name is a row nobody can find again.
  if (base.firstName.trim() !== next.firstName.trim()) changes.firstName = next.firstName.trim();

  text('lastName', 'lastName');
  text('email', 'email');
  text('phone', 'phone');
  text('mobile', 'mobile');
  text('jobTitle', 'jobTitle');
  text('document', 'document');

  if (base.companyId !== next.companyId) changes.companyId = next.companyId;
  if (base.sourceId !== next.sourceId) changes.sourceId = next.sourceId;

  if (base.personType !== next.personType) {
    changes.personType = next.personType === '' ? null : next.personType;
  }

  if (base.lifecycleStage !== next.lifecycleStage) changes.lifecycleStage = next.lifecycleStage;

  return Object.keys(changes).length > 0 ? changes : null;
}

/** Whether there is anything unsaved, for the guard that asks before throwing it away. */
export function isDirty(base: ContactForm, next: ContactForm): boolean {
  return changesBetween(base, next) !== null;
}
